import { FC, useEffect, useState } from 'react';
import { IconButton, Stack, TableRow, Tooltip, Typography } from '@mui/material';
import { TableCell } from './TableCell';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { DocumentType, FragmentType, getFragmentData, graphql } from '../../../gql';
import { useApiMutation } from '../../../query/hooks';
import { journeyDetailSchema } from '../JourneyDetailPage';
import { PhaseMetricCell } from './PhaseMetricCell';
import { DeleteOutline, EditOutlined, MoreHoriz, OpenInFull } from '@mui/icons-material';
import { CustomPopover } from '../../../ui/CustomPopover';
import { useDrawerContext } from '../../../drawer/DrawerContext';
import { useTypedPathParams } from '../../../../../shared/fe/hooks/useTypedPathParams';
import { useModalContext } from '../../../modal/ModalContext';
import { ThemeMode } from '../../../types/config';
import { AddPhaseDividerButton } from '../../utils/AddPhaseDividerButton';
import { useLocation } from 'react-router';
import slugify from 'slugify';
import { useJourneyFilters } from '../../../journey/useJourneyFilters';
import { getRangesWithDefault } from './widgets/metrics/metricRange';
import { Editor } from '../../../components/wysiwyg/Editor';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { useAtom } from 'jotai';
import { topRowHeightAtom } from '../../summary/SummaryBanner';
import { useZoom } from '../../timeline/helpers/useZoom';

type Props = Readonly<{
	phasesData: readonly FragmentType<typeof journeyDetail_phases>[];
	hideFirstCell?: boolean;
	ignoreColspan?: boolean;
	tableHeight?: number;
	extendedOffset?: boolean;
}>;

const journeyDetail_phases = graphql(`
	fragment journeyDetail_phases on Phase {
		id
		name
		description
		contentMetrics {
			id
			activities {
				name
			}
			metric {
				id
				unitSign
				dataSet {
					metricTemplate {
						unitSign
					}
				}
			}
		}
		touchpoints(filter: { type: { eq: activity } }) {
			id
			activity {
				...activityCard_activity
			}
		}
		...journeyFilters_phases
	}
`);

type PhaseFragment = DocumentType<typeof journeyDetail_phases>;

const detail_reorderPhases = graphql(`
	mutation detail_reorderPhases($journeyId: UUID!, $phases: [JourneyUpdatePhasesEntityRelationInput!]!) {
		updateJourney(by: { id: $journeyId }, data: { phases: $phases }) {
			ok
			errorMessage
		}
	}
`);

const getMetrics = (phase: PhaseFragment) => {
	return phase.contentMetrics
		.map((contentMetric) => contentMetric)
		.filter((metric) => {
			return metric.metric?.id != null;
		});
};

const TopRow: FC<Props> = ({ phasesData, tableHeight, hideFirstCell = false, ignoreColspan = false, extendedOffset }) => {
	const { id: journeyId } = useTypedPathParams(journeyDetailSchema);
	const [, setOffset] = useAtom(topRowHeightAtom);
	const theme = useTheme();
	const intl = useIntl();
	const { pathname } = useLocation();
	const phases = getFragmentData(journeyDetail_phases, phasesData);
	const [indexEntered, setIndexEntered] = useState(-1);
	const { setDrawerWithoutPrevParams } = useDrawerContext();
	const { mutateAsync: reorderPhases } = useApiMutation(detail_reorderPhases);
	const [rowHeight, setRowHeight] = useState(0);
	const { setModal } = useModalContext();
	const [phasesState, setPhasesState] = useState(phases);
	const [filters] = useJourneyFilters();
	const {
		compareRange: { start: compareStart, end: compareEnd },
		targetRange: { start: targetStart, end: targetEnd },
	} = getRangesWithDefault(filters.date);
	const [zoom] = useZoom();

	useEffect(() => {
		setPhasesState(phases);
	}, [phases]);

	useEffect(() => {
		const rowRef = document.querySelector('#top-row');

		if (rowRef) {
			const resizeObserver = new ResizeObserver((data) => {
				setRowHeight(data[0].contentRect.height);
				setOffset(data[0].contentRect.height);
			});

			resizeObserver.observe(rowRef);

			return () => {
				resizeObserver.unobserve(rowRef);
			};
		}
	});

	const handleReorderColumns = (result: DropResult) => {
		const { source, destination } = result;

		if (destination == null) {
			return;
		}

		const newState = [...phasesState];
		const itemToInsertToNewPosition = newState[source.index];

		newState.splice(source.index, 1);
		newState.splice(destination.index, 0, itemToInsertToNewPosition);

		setPhasesState(newState);

		reorderPhases({
			journeyId: journeyId,
			phases: newState.map((phase, index) => {
				return {
					update: {
						by: {
							id: phase.id,
						},
						data: { order: index },
					},
				};
			}),
		});
	};

	const onDragEnd = async (result: DropResult) => {
		const { source, destination } = result;

		if (!destination) {
			return;
		}

		if (destination.index === source.index) {
			return;
		}

		if (destination.index !== source.index) {
			await handleReorderColumns(result);
			return;
		}
	};

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable droppableId={'COLUMNS'} direction={'horizontal'}>
				{(provided) => (
					<TableRow ref={provided.innerRef} id={'top-row'} {...provided.droppableProps}>
						{!hideFirstCell ? (
							<TableCell header isSticky>
								<FormattedMessage id={'detail.table.phase'} />
							</TableCell>
						) : null}
						{phasesState.map((phase, index) => {
							const isLastPhase = index === phasesState.length - 1;
							const metrics = getMetrics(phase);

							return (
								<Draggable key={phase.id} draggableId={phase.id} index={index}>
									{(provided) => (
										<TableCell
											key={phase.id}
											colSpan={ignoreColspan ? 1 : phase.touchpoints.length}
											onMouseEnter={() => setIndexEntered(index)}
											onMouseLeave={() => {
												if (indexEntered === index) {
													setIndexEntered(-1);
												}
											}}
											sx={{
												position: 'relative',
												borderRight: isLastPhase ? undefined : `2px solid transparent`,
												verticalAlign: 'top',
												borderTop: `1px solid ${theme.palette.grey[200]}`,
												backgroundColor: theme.palette.mode === ThemeMode.LIGHT && index % 2 !== 0 ? '#FBFBFB' : 'transparent',
												px: 3,
												py: 2,
											}}
											data-phase={slugify(phase.name, { lower: true })}
											dndRef={provided.innerRef}
											{...provided.dragHandleProps}
											{...provided.draggableProps}
										>
											<Stack direction={'row'} height={'100%'} justifyContent={'flex-start'} gap={3} position={'relative'}>
												<Stack flex={'1'} minWidth={400} maxWidth={500} position={'relative'}>
													<Typography variant={'h2'} fontWeight={700}>
														{phase.name}
													</Typography>
													{zoom > 0.5 && (
														<Typography color={theme.palette.grey[500]}>
															{phase.description ? <Editor readonly state={phase.description} /> : <>&nbsp;</>}
														</Typography>
													)}
													<Stack
														flexDirection={'row'}
														gap={1.5}
														alignItems={'center'}
														alignSelf={'flex-start'}
														position={'absolute'}
														top={-8}
														right={0}
														borderRadius={1}
														padding={'2px'}
													>
														{indexEntered === index && (
															<>
																<Tooltip title={intl.formatMessage({ id: 'phase.delete' })}>
																	<CustomPopover
																		layoutIcon={<MoreHoriz />}
																		options={[
																			{
																				icon: <EditOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
																				caption: <FormattedMessage id={'common.edit'} />,
																				onAction: () => setDrawerWithoutPrevParams('editPhase', { phaseId: phase.id }),
																			},
																			{
																				icon: <DeleteOutline sx={{ fontSize: 16, fill: theme.palette.error.main }} />,
																				caption: (
																					<Typography color={theme.palette.error.main}>
																						<FormattedMessage id={'common.delete'} />
																					</Typography>
																				),
																				onAction: () => setModal({ type: 'deletePhase', payload: { id: phase.id, name: phase.name } }),
																			},
																		]}
																	/>
																</Tooltip>
																<IconButton color={'default'} onClick={() => {}} size={'small'}>
																	<OpenInFull sx={{ width: 18, height: 18 }} />
																</IconButton>
															</>
														)}
													</Stack>
												</Stack>
												{metrics.length > 0 && zoom > 0.5 && (
													<Stack
														position={'sticky'}
														minWidth={metrics.length * 100}
														left={extendedOffset ? 230 : 20}
														direction={'row'}
														gap={1.5}
													>
														{metrics.map(({ metric, activities }) => (
															<PhaseMetricCell
																key={metric?.id}
																metricId={metric?.id}
																compareStart={compareStart}
																compareEnd={compareEnd}
																targetStart={targetStart}
																targetEnd={targetEnd}
																activities={activities.map((activity) => activity.name)}
																unitSign={metric?.unitSign ?? metric?.dataSet?.metricTemplate?.unitSign ?? null}
															/>
														))}
													</Stack>
												)}
											</Stack>
											{index === 0 ? null : (
												<AddPhaseDividerButton
													tableHeight={tableHeight ?? 0}
													phaseId={phasesState[index - 1].id}
													isTimeline={pathname.includes('activity-map')}
													buttonOffset={rowHeight}
												/>
											)}
										</TableCell>
									)}
								</Draggable>
							);
						})}
					</TableRow>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export { TopRow };
